import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { REGISTER_URL } from "@/data/plans";
import { scrollToHash } from "@/components/Navbar";

const EXPLORE = [
  ["Challenges", "#challenges"],
  ["Rules", "#rules"],
  ["Mentorship", "#mentorship"],
  ["Max Capital", "#scaling"],
  ["FAQ", "#faq"],
  ["Swing 2 Step FAQ", "/swing-faq"],
];

const LEGAL = [
  ["Contact Us", "/contact"],
  ["Terms & Conditions", "/terms"],
  ["Funded Account Terms", "/funded-terms"],
  ["Privacy Policy", "/privacy"],
  ["Client Privacy Notice", "/client-privacy"],
  ["Rules", "/rules"],
];

const DisclaimerBlock = ({ title, children }) => (
  <div className="mb-8">
    <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-[#FFD700] mb-3">{title}</h4>
    <div className="text-[11px] leading-relaxed text-[#999999]/70 space-y-2">{children}</div>
  </div>
);

export default function Footer() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const go = (e, href) => {
    e.preventDefault();
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToHash(href), 500);
    } else {
      scrollToHash(href);
    }
  };

  return (
    <footer className="border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-[-0.05em] text-white leading-[1.1]">
          Ready to trade <span className="gold-text">easy?</span>
        </h2>
        <p className="mt-4 text-base text-[#999999] max-w-md mx-auto">
          Pick a challenge, join the Discord, and start your path to $400K in funded capital.
        </p>
        <a
          href={REGISTER_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-cta-button"
          className="btn-gold group inline-flex items-center gap-2 rounded-full px-8 py-4 text-base font-bold mt-8"
        >
          Choose Your Challenge
          <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
        </a>
      </div>

      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <div className="text-lg font-bold tracking-[-0.04em] text-white mb-3">
              my<span className="gold-text">FX</span>Pocket
            </div>
            <p className="text-sm text-[#999999] leading-relaxed max-w-xs">
              Trade Easy. No Bull. Prop trading challenges backed by 5 years of industry trust,
              free mentorship, and Match-Trader technology.
            </p>
            <a
              href="mailto:admin@myfxpocket.com"
              data-testid="footer-email-link"
              className="inline-block mt-4 text-sm text-[#FFD700] hover:underline"
            >
              admin@myfxpocket.com
            </a>
          </div>
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-white mb-4">Explore</h4>
            <nav className="flex flex-col gap-2.5">
              {EXPLORE.map(([label, href]) =>
                href.startsWith("/") ? (
                  <Link
                    key={href}
                    to={href}
                    data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="text-sm text-[#999999] hover:text-[#FFD700] transition-colors"
                  >
                    {label}
                  </Link>
                ) : (
                  <a
                    key={href}
                    href={href}
                    data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={(e) => go(e, href)}
                    className="text-sm text-[#999999] hover:text-[#FFD700] transition-colors"
                  >
                    {label}
                  </a>
                )
              )}
            </nav>
          </div>
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-white mb-4">
              Legal & Policies
            </h4>
            <nav className="flex flex-col gap-2.5">
              {LEGAL.map(([label, to]) => (
                <Link
                  key={to}
                  to={to}
                  data-testid={`footer-legal-${to.slice(1)}`}
                  className="text-sm text-[#999999] hover:text-[#FFD700] transition-colors"
                >
                  {label}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </div>

      <div className="border-t border-white/5 bg-[#0D0E13]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 lg:grid-cols-2 gap-x-14" data-testid="footer-legal-disclaimers">
          <div>
            <DisclaimerBlock title="No Investment Services Disclaimer">
              <p>
                MyFXPocket and/or FX-EDGE V Ltd (as part of these services) does not provide any
                investment services, brokerage services, portfolio management, execution of client
                orders, custody of funds or assets, or investment advice. The services offered are
                limited to proprietary evaluation, assessment and simulated trading programs based
                on demo accounts only.
              </p>
            </DisclaimerBlock>
            <DisclaimerBlock title="Relationship and Service Providers">
              <p>
                All challenge and assessment programs available on this website are offered and
                operated by FX-EDGE V Ltd, a company organized under the laws of Vanuatu, registered
                office at 1276, Govant Building, Kumul Highway, Port Vila, Vanuatu (company no.
                700604). Certain branding, marketing, community and customer-facing activities are
                carried out under the trade name MyFXPocket, acting as a commercial brand or partner
                of FX-EDGE V Ltd. FX-EDGE V Ltd is the sole provider of the program framework,
                trading environment, execution, risk management systems and account infrastructure.
                The trading platform is Match-Trader, provided by Match-Trade Technologies LLC.
                Payment settlement is handled by Odeonpay ALE S.R.L. (&quot;Paysagi&quot;), acting
                as Merchant of Record.
              </p>
            </DisclaimerBlock>
            <DisclaimerBlock title="Restricted Jurisdictions">
              <p>
                The Services are made available on a cross-border, reverse-solicitation basis only.
                Services are not provided to citizens and/or residents of territories where prop
                trading is restricted by local laws or regulations, including:
              </p>
              <p>
                Afghanistan | Belarus | Crimea / Donetsk / Luhansk / Kherson / Zaporizhzhia /
                Sevastopol region | Cuba
              </p>
              <p>Democratic Republic of Korea | Gaza | Iran | Libya</p>
              <p>Myanmar | Russian Federation | South Sudan | North Korea</p>
              <p>Syria | Venezuela</p>
            </DisclaimerBlock>
          </div>
          <div>
            <DisclaimerBlock title="Risk Disclosure">
              <p>
                Trading strategies and methodologies assessed through the Services involve a high
                degree of risk and may not be suitable for all individuals. Participation in the
                Services does not involve the investment of your own capital; however, trading in
                real market conditions involves substantial risk.
              </p>
              <p className="italic">Past or simulated performance is not indicative of future results.</p>
            </DisclaimerBlock>
            <DisclaimerBlock title="Fees and Refund Policy">
              <p>
                Participation in challenge or assessment programs requires payment of a one-time fee.
                Fees are charged for access to the evaluation service and are generally{" "}
                <strong className="text-[#DBDFE8]">non-refundable</strong> once access has been
                granted, except where a refund is required under applicable mandatory consumer
                protection laws.
              </p>
              <p>
                By purchasing a challenge, you acknowledge that you are paying for access to an
                evaluation service and{" "}
                <strong className="text-[#DBDFE8]">
                  not for any guarantee of success, funding, compensation, or future participation
                  in funded programs.
                </strong>
              </p>
              <p>
                Fees are payable to FX-EDGE V LTD or to authorised third-party payment service
                providers acting on its behalf, including payment processors or merchants of record.
                FX-EDGE V LTD does not provide payment services and does not store payment card or
                wallet credentials. You agree not to initiate chargebacks or payment disputes
                without first contacting us and allowing a reasonable opportunity to investigate and
                resolve the issue. Any refund issued shall be limited to the amount actually paid
                for the relevant service and shall not include compensation for losses, missed
                opportunities, consequential damages or any past payments related to other
                challenges.
              </p>
            </DisclaimerBlock>
          </div>
        </div>
        <div className="border-t border-white/5">
          <p className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-[11px] text-[#999999]/40 text-center">
            © {new Date().getFullYear()} MyFXPocket. Trade Easy. No Bull.
          </p>
        </div>
      </div>
    </footer>
  );
}
